import { useState } from 'react';
import { Alert, Box, Button, Group, Modal, Paper, Stack, Switch, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { settingsApi, TenantSettings } from '@/services/settings.api';
import { getCurrentUserRole } from '@/features/auth/currentUser';
import { OfflineKitSection } from '../components/OfflineKitSection';

const NAVY = '#415A91';

/**
 * Workspace configuration.
 *
 * Deliberately separate from the Library (variables, rich text presets): those
 * are authoring tools reached constantly, these are settings changed rarely and
 * only by admins. Sections are listed vertically so adding the next one — API
 * keys, billing, audit log — does not require rethinking the layout.
 */
export default function SettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = getCurrentUserRole() === 'ADMIN';

  /**
   * The value the admin is asking to switch to, or null when nothing is
   * pending. Changing this rule changes how sharing behaves for everyone in the
   * workspace, so it is confirmed rather than applied on a stray click.
   *
   * The Switch stays bound to the saved value throughout, so cancelling needs
   * no reverting — it never moved.
   */
  const [pendingShareLinkChange, setPendingShareLinkChange] = useState<boolean | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });

  const update = useMutation({
    mutationFn: (next: Partial<TenantSettings>) => settingsApi.update(next),
    onSuccess: (saved) => {
      queryClient.setQueryData(['settings'], saved);
      // The editor reads this to decide what the share button does, so drop its
      // cached copy rather than leaving it on the old rule.
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setPendingShareLinkChange(null);
      notifications.show({ color: 'green', message: 'Settings saved' });
    },
    onError: (error: any) => {
      setPendingShareLinkChange(null);
      notifications.show({
        color: 'red',
        title: 'Settings were not saved',
        message: error?.response?.data?.message || error?.message || 'Something went wrong.',
      });
    },
  });

  return (
    <Box p="xl">
      <Title order={2} mb={4}>Settings</Title>
      <Text size="sm" c="dimmed" mb="xl">
        Configuration that applies to everyone in this workspace.
      </Text>

      {!isAdmin && (
        <Alert icon={<IconAlertCircle size={16} />} color="blue" mb="lg">
          Only admins can change workspace settings. These are shown read-only.
        </Alert>
      )}

      <Stack gap="lg" maw={720}>
        <Paper withBorder p="lg" radius="md">
          <Title order={4} mb="xs">Sharing</Title>

          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xl">
            <Box>
              <Text size="sm" fw={500}>Allow multiple share links per flow</Text>
              <Text size="xs" c="dimmed" mt={4}>
                Off means a flow has one share link. Sharing it again returns the
                same URL rather than creating another, and the link always shows
                the latest published version. Turn this on only if you need
                separate links for different audiences.
              </Text>
            </Box>

            <Switch
              checked={settings?.allowMultipleShareLinks ?? false}
              disabled={!isAdmin || isLoading || update.isPending}
              onChange={(e) => setPendingShareLinkChange(e.currentTarget.checked)}
              color={NAVY}
              styles={{ root: { flexShrink: 0 } }}
            />
          </Group>
        </Paper>

        <OfflineKitSection isAdmin={isAdmin} />

        <Paper withBorder p="lg" radius="md">
          {/* Named to match the destination page and the breadcrumb — one name
              for one thing, rather than People here and Members there. */}
          <Title order={4} mb="xs">Members</Title>
          <Group justify="space-between" align="center" wrap="nowrap" gap="xl">
            <Text size="xs" c="dimmed">
              Invite teammates, change their roles, and remove access.
            </Text>
            <Text
              size="sm"
              fw={600}
              c={NAVY}
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              onClick={() => navigate('/settings/members')}
            >
              <Group gap={6} wrap="nowrap">
                <IconUsers size={16} />
                Manage members
              </Group>
            </Text>
          </Group>
        </Paper>
      </Stack>

      {/* Confirmation. The two directions are not mirror images — enabling
          loosens a guarantee going forward, while disabling changes what the
          share button does without touching links already issued — so each
          states its own consequence rather than sharing vague copy. */}
      <Modal
        opened={pendingShareLinkChange !== null}
        onClose={() => setPendingShareLinkChange(null)}
        title={
          <Text fw={600}>
            {pendingShareLinkChange
              ? 'Allow multiple share links?'
              : 'Limit each flow to one share link?'}
          </Text>
        }
        centered
      >
        <Stack gap="md">
          {pendingShareLinkChange ? (
            <Text size="sm" c="#4E5A6B">
              Every flow will be able to hold more than one active share link, and
              new links can be pinned to a specific version. Links already issued
              keep working exactly as they do now.
            </Text>
          ) : (
            <Text size="sm" c="#4E5A6B">
              Sharing a flow will return its existing link instead of creating
              another, and that link will follow the latest published version.
              Nothing is revoked: flows that already have several links keep them
              all, but cannot create more until they are revoked down to one.
            </Text>
          )}

          <Text size="sm" c="#4E5A6B">
            This applies to everyone in the workspace.
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" onClick={() => setPendingShareLinkChange(null)}>
              Cancel
            </Button>
            <Button
              loading={update.isPending}
              style={{ backgroundColor: NAVY }}
              onClick={() =>
                update.mutate({ allowMultipleShareLinks: pendingShareLinkChange as boolean })
              }
            >
              {pendingShareLinkChange ? 'Allow multiple links' : 'Limit to one link'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
